import { useMemo } from 'react';
import { toast } from 'react-hot-toast';

// Import constants
import { MAX_PARTICIPANTS_NAME_LENGTH } from '../../constants/schedule';

// Import types
import type { EventByUser } from '../../types/schedule';

// Import components
import CloseButton from '../button/CloseButton';

type Props = {
  // ID-centric
  users: string[]; // userIds
  currentUser: string; // userId
  userIdToName: Record<string, string>;
  ownerByUserId: Record<string, string>; // userId -> deviceId
  ownsParticipant: (ownerDeviceId: string | undefined) => boolean;
  // Derived, for small UI copy only
  joinName: string;
  isOverlapTimes: boolean;
  hoveredUsers?: string[]; // userIds
  maxParticipants: number;
  setCurrentUser: (uid: string) => void; // set current userId
  setJoinName: (name: string) => void;
  eventsByUser: EventByUser;
  onRemoveUser: (userId: string) => void;
  onRenameUser: (userId: string, newName: string) => void;
  onJoin: () => void;
};

const ParticipantsPanel = (props: Props) => {
  const {
    users,
    currentUser,
    userIdToName,
    ownerByUserId,
    ownsParticipant,
    joinName,
    isOverlapTimes,
    hoveredUsers = [],
    maxParticipants,
    setCurrentUser,
    setJoinName,
    eventsByUser,
    onRemoveUser,
    onRenameUser,
    onJoin,
  } = props;

  const filledCount = useMemo(() => {
    let count = 0;
    for (const u of users) {
      if ((eventsByUser[u] || []).length > 0) count++;
    }
    return count;
  }, [users, eventsByUser]);

  return (
    <div>
      {/* Participants title and count */}
      <div className="text-base font-semibold text-gray-700 mb-2">
        <div>
          Participants ({filledCount}/{users.length})
        </div>
      </div>

      {/* Participants list */}
      <div className="space-y-1">
        {users.map((userId) => {
          const isCurrent = userId === currentUser;
          const isHovered = isOverlapTimes && hoveredUsers.includes(userId);
          const hasHoverFilter = isOverlapTimes && hoveredUsers.length > 0; // only when hovering the best overlap green cell
          const isFilteredOut = hasHoverFilter && !isHovered;
          return (
            <div
              key={userId}
              className={
                'flex items-center justify-between text-xs px-2 py-1 rounded border ' +
                (isOverlapTimes
                  ? 'bg-white border-gray-300'
                  : isCurrent
                    ? 'bg-green-100 border-green-600'
                    : 'bg-white border-gray-300 hover:bg-gray-50')
              }>
              {/* User name */}
              <button
                className={
                  'flex-1 text-left px-2 py-2.5 min-w-0 ' +
                  (isOverlapTimes
                    ? 'cursor-not-allowed ' +
                      (hasHoverFilter && isHovered ? '' : 'opacity-60')
                    : '')
                }
                disabled={isOverlapTimes}
                aria-disabled={isOverlapTimes}
                onClick={() => {
                  if (isOverlapTimes) return;
                  setCurrentUser(userId);
                }}>
                <div className="flex flex-col">
                  <span
                    className={
                      'text-sm font-medium text-gray-900 truncate ' +
                      (isHovered
                        ? 'text-emerald-700 font-semibold underline'
                        : '') +
                      (isFilteredOut ? ' line-through' : '')
                    }>
                    {userIdToName[userId] || userId}
                  </span>
                  <span className="text-[10px] text-gray-600 truncate"></span>
                </div>
              </button>

              {/* Edit/Remove buttons */}
              {ownsParticipant(ownerByUserId[userId]) ? (
                <div className="flex items-center gap-1">
                  <CloseButton
                    ariaLabel="Rename this user (you created it)"
                    variant="info"
                    showIcon={false}
                    className="static text-sm font-semibold border"
                    onClose={() => {
                      const msg = 'Enter a new name:';
                      const newName = prompt(msg, userIdToName[userId]);

                      if (newName === null) return;

                      if (newName.length > MAX_PARTICIPANTS_NAME_LENGTH)
                        return toast.error(
                          `Name must be less than ${MAX_PARTICIPANTS_NAME_LENGTH} characters`,
                        );

                      onRenameUser(userId, newName);
                    }}>
                    Edit
                  </CloseButton>
                  <CloseButton
                    ariaLabel="Remove this user (you created it)"
                    variant="danger"
                    className="static border"
                    iconClassName="h-4 w-4"
                    onClose={() => {
                      // Confirmation dialog
                      const name = userIdToName[userId] || userId;
                      const msg = `Remove user "${name}" and all their availability?`;
                      const ok = confirm(msg);
                      if (!ok) return;

                      onRemoveUser(userId);
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Join controls */}
      <div className="flex items-center gap-2 mt-3 ">
        <input
          className={
            'text-base px-2 py-2 border rounded w-full hover:bg-gray-100 ' +
            (isOverlapTimes ? 'opacity-60 cursor-not-allowed' : '')
          }
          aria-disabled={isOverlapTimes}
          placeholder={isOverlapTimes ? 'Disabled in Overlap' : 'Enter name'}
          value={joinName}
          onChange={(e) => {
            if (isOverlapTimes) return;
            setJoinName(e.target.value);
          }}
          disabled={isOverlapTimes}
          maxLength={MAX_PARTICIPANTS_NAME_LENGTH}
        />
        <button
          onClick={() => {
            if (isOverlapTimes) return;
            onJoin();
          }}
          className={
            'text-base font-bold px-2 py-2 border rounded bg-white hover:bg-gray-100 ' +
            (isOverlapTimes ? 'opacity-60 cursor-not-allowed' : '')
          }
          disabled={isOverlapTimes}
          aria-disabled={isOverlapTimes}
          title={
            isOverlapTimes ? 'Disabled in overlapping times mode' : undefined
          }>
          Join
        </button>
      </div>

      {/* Max participants count */}
      <div className="text-base text-gray-500 mt-1">
        {users.length}/{maxParticipants}
      </div>
    </div>
  );
};

export default ParticipantsPanel;
